import { Fragment, memo, useCallback, useState } from "react";
import PropTypes from "prop-types";
import { quotationActivityShape } from "app/utils/propTypes";
import { FormattedMessage } from "react-intl";
import ProgressiveImg from "app/pages/.shared/ProgressiveImg";
import Paragraphs from "app/pages/.shared/Paragraphs";
import MoreDetails from "app/pages/.shared/MoreDetails";
import AmountContainer from "app/pages/.shared/AmountContainer";
import get from "lodash/get";

import "./QuotationActivityHeader.scss";
import AmountSign from "app/pages/.shared/AmountSign/AmountSign";
import Badge from "app/pages/.shared/Badge";
import { sendTagOnActivityLessDetails, sendTagOnActivityMoreDetails } from "app/utils/analytics";
import Button, { BUTTON_VARIANTS } from "app/pages/.shared/form/Button";

const IMG_SIZE = {
	small: { width: 200, height: 100 },
	medium: { width: 200, height: 100 },
	large: { width: 225, height: 130 },
	xlarge: { width: 225, height: 130 },
};

const QuotationActivityHeader = ({ activity, total, minGuestPrice, hasIncludedGuest }) => {
	const [showDetails, setShowDetails] = useState(false);

	const showDetailsLabel = showDetails ? (
		<FormattedMessage id="product.page.less.details" />
	) : (
		<FormattedMessage id="product.page.more.details" />
	);

	const photoUrl = get(activity, "photo.url");
	const photoAlt = get(activity, "photo.alt");
	const photoThumbnail = get(activity, "photo.thumbnail");
	const hasPragraphs = get(activity, "paragraphs.length") > 0;

	let priceNode = (
		<div className="quotation-activity__starting-price">
			<FormattedMessage
				id="quotation.activity.guest.price.label.1"
				values={{
					price: <AmountContainer amount={minGuestPrice} />,
				}}
			/>
		</div>
	);

	if (hasIncludedGuest && total === 0) {
		priceNode = (
			<div className="quotation-activity__starting-price">
				<Badge>
					<FormattedMessage id="general.included" />
				</Badge>
			</div>
		);
	} else if (total > 0) {
		priceNode = (
			<div className="quotation-activity__total">
				<AmountContainer prefix={<AmountSign sign="+" />} amount={total} />
			</div>
		);
	}

	const handleDetailToggle = useCallback(() => {
		setShowDetails(value => {
			const shouldShow = !value;
			if (shouldShow) {
				sendTagOnActivityMoreDetails();
			} else {
				sendTagOnActivityLessDetails();
			}
			return shouldShow;
		});
	}, []);

	return (
		<Fragment>
			<div className="quotation-activity__header">
				{photoUrl && (
					<div className="quotation-activity__photo">
						<ProgressiveImg
							sizes={IMG_SIZE}
							src={photoUrl}
							alt={photoAlt}
							thumbnail={photoThumbnail}
						/>
					</div>
				)}

				<div className="quotation-activity__details">
					<div className="quotation-activity__label">{activity.name}</div>
					{hasPragraphs && (
						<Button
							variant={BUTTON_VARIANTS.BORDERLESS}
							className="quotation-activity__details-button"
							onClick={handleDetailToggle}
							label={showDetailsLabel}
						/>
					)}
				</div>

				{priceNode}
			</div>
			<div className="quotation-activity__description">
				{hasPragraphs && (
					<MoreDetails expanded={showDetails}>
						<div className="quotation-activity__content">
							<div className="quotation-activity__paragraphs">
								<Paragraphs
									paragraphs={activity.paragraphs}
									enableHTMLFormatting={true}
								/>
							</div>
						</div>
					</MoreDetails>
				)}
			</div>
		</Fragment>
	);
};

QuotationActivityHeader.propTypes = {
	activity: quotationActivityShape,
	total: PropTypes.number,
	minGuestPrice: PropTypes.number,
	hasIncludedGuest: PropTypes.bool,
};

QuotationActivityHeader.defaultProps = {
	activity: {},
};

export default memo(QuotationActivityHeader);
